import { Box, CircularProgress } from "@mui/material"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import { useFormik } from "formik"
import { useAppDispatch, useAppSelector } from "hooks"
import { useState } from "react"
import licensePoolService from "services/licensePoolService"
import licenseService from "services/licenseService"
import { setCurrentLicensePool, updateLicensePool } from "slices/licensePoolSlice"
import { addLicenses } from "slices/licenseSlice"
import type { ICreateLicense } from "types/licenseTypes"
import * as Yup from "yup"

export function CreateLicenseDialog() {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [open, setOpen] = useState(false)

  const currentLicensePool = useAppSelector((state) => state.licensepool.currentLicensePool)
  const dispatch = useAppDispatch()

  const initialValues: ICreateLicense = {
    licensePoolId: "",
    numberOfLicenses: 0,
  }

  const createLicenseValidationScheme = Yup.object({
    numberOfLicenses: Yup.number().required("Please enter number of new licenses"),
  })

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: createLicenseValidationScheme,
    onSubmit: (values) => {
      if (currentLicensePool === undefined) {
        return
      }

      setIsLoading(true)

      const newLicense: ICreateLicense = {
        licensePoolId: currentLicensePool.id,
        numberOfLicenses: values.numberOfLicenses,
      }

      licenseService
        .createLicense(newLicense)
        .then((licenses) => {
          dispatch(addLicenses(licenses))

          licensePoolService
            .getLicensePool(currentLicensePool.id)
            .then((licensePool) => {
              dispatch(updateLicensePool(licensePool))
              dispatch(setCurrentLicensePool(licensePool))
              setIsLoading(false)
              handleClose()
            })
            .catch((err) => {
              console.log(err)
              setIsLoading(false)
            })
        })
        .catch((err) => {
          console.log(err)
          setIsLoading(false)
        })
    },
  })

  const handleClickOpen = () => {
    formik.resetForm()
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <div>
      <Button variant="contained" onClick={handleClickOpen}>
        Add Licenses
      </Button>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>Add New Licenses</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              id="numberOfLicenses"
              label="Number of licenses"
              type="number"
              fullWidth
              variant="outlined"
              value={formik.values.numberOfLicenses}
              onChange={formik.handleChange}
              error={formik.touched.numberOfLicenses && Boolean(formik.errors.numberOfLicenses)}
              helperText={formik.touched.numberOfLicenses && formik.errors.numberOfLicenses}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Box sx={{ m: 1, position: "relative" }}>
              <Button type="submit" disabled={isLoading}>
                Create
              </Button>
              {isLoading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  )
}
